import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { LogtoProvider, useLogto } from '@logto/react';
import { AUTH_CONFIG, ADVANCED_CONFIG } from '../config';
import { api } from '../utils/api';
import { createLoginFailedPayload, createLoginStartedPayload } from '../utils/authFunnel';

// User and Creator interfaces
const createUser = (data = {}) => ({
  id: data.id || '',
  username: data.username || '',
  email: data.email || '',
  first_name: data.first_name || '',
  last_name: data.last_name || '',
  display_name: data.display_name || data.username || data.email || '',
  is_active: data.is_active !== undefined ? data.is_active : true,
  date_joined: data.date_joined || new Date().toISOString(),
});

const createCreator = (data = {}) => ({
  id: data.id || '',
  display_name: data.display_name || '',
  plan: data.plan || 'free',
  credits: data.credits || 0,
  created_at: data.created_at || new Date().toISOString(),
  updated_at: data.updated_at || new Date().toISOString(),
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Guest Mode Hook
export const useGuestMode = () => {
  const [guestUsage, setGuestUsage] = useState(() => {
    const stored = localStorage.getItem('guest_usage');
    return stored ? JSON.parse(stored) : { count: 0, lastReset: Date.now() };
  });

  const checkGuestQuota = useCallback(() => {
    const now = Date.now();
    const dayInMs = 24 * 60 * 60 * 1000;

    // Reset daily quota
    if (now - guestUsage.lastReset > dayInMs) {
      const resetUsage = { count: 0, lastReset: now };
      setGuestUsage(resetUsage);
      localStorage.setItem('guest_usage', JSON.stringify(resetUsage));
      return true;
    }

    return guestUsage.count < AUTH_CONFIG.UI.GUEST_QUOTA;
  }, [guestUsage]);

  const incrementGuestUsage = useCallback(() => {
    const newUsage = { ...guestUsage, count: guestUsage.count + 1 };
    setGuestUsage(newUsage);
    localStorage.setItem('guest_usage', JSON.stringify(newUsage));
  }, [guestUsage]);

  return {
    canUseAsGuest: checkGuestQuota(),
    remainingQuota: Math.max(0, AUTH_CONFIG.UI.GUEST_QUOTA - guestUsage.count),
    incrementUsage: incrementGuestUsage
  };
};

// Inner Auth Provider
const InnerAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [creator, setCreator] = useState(null);
  const [error, setError] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileFetched, setProfileFetched] = useState(false);
  const [currentToken, setCurrentToken] = useState(null);

  const {
    isAuthenticated: logtoAuthenticated,
    getAccessToken: getLogtoToken,
    getIdTokenClaims,
    signIn: logtoSignIn,
    signOut: logtoSignOut,
    isLoading
  } = useLogto();

  // Fetch user profile
  const fetchUserProfile = useCallback(async () => {
    try {
      setProfileLoading(true);
      setError(null);

      // Try to get user info from ID token claims
      try {
        const idTokenClaims = await getIdTokenClaims();
        if (idTokenClaims) {
          console.log('Using ID token claims:', idTokenClaims);

          const userData = createUser({
            id: String(idTokenClaims.sub || ''),
            username: String(idTokenClaims.email || idTokenClaims.username || ''),
            email: String(idTokenClaims.email || ''),
            first_name: String(idTokenClaims.given_name || ''),
            last_name: String(idTokenClaims.family_name || ''),
            display_name: String(idTokenClaims.name || idTokenClaims.username || idTokenClaims.email || ''),
          });

          setUser(userData);
          setCreator(null); // Create default creator if needed
          setProfileFetched(true);

          // Set the token for API requests
          if (idTokenClaims.sub) {
            setCurrentToken(`logto_id_${idTokenClaims.sub}`);
          }
          return;
        }
      } catch (idTokenError) {
        console.warn('Failed to get user info from ID token:', idTokenError);
      }

      setProfileFetched(true);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch profile');
      setProfileFetched(true);
    } finally {
      setProfileLoading(false);
    }
  }, [getLogtoToken, getIdTokenClaims]);

  // Handle auth state changes
  useEffect(() => {
    setProfileFetched(false);
    if (!logtoAuthenticated) {
      setCurrentToken(null);
      setUser(null);
      setCreator(null);
    }
  }, [logtoAuthenticated]);

  useEffect(() => {
    if (logtoAuthenticated && !user && !profileLoading && !profileFetched) {
      fetchUserProfile();
    }
  }, [logtoAuthenticated, user, profileLoading, profileFetched, fetchUserProfile]);

  // Auth actions
  const signIn = async () => {
    try {
      setError(null);
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/register') {
        sessionStorage.setItem('intendedPath', currentPath);
      }
      void api.trackFunnelEvent('login.started', createLoginStartedPayload({
        surface: 'auth',
        reason: 'user_requested',
        path: currentPath,
      }));
      await logtoSignIn(`${window.location.origin}${AUTH_CONFIG.UI.LOGIN_REDIRECT}`);
    } catch (error) {
      console.error('Sign in failed:', error);
      void api.trackFunnelEvent('login.failed', createLoginFailedPayload({
        reason: 'redirect_error',
        error,
        path: window.location.pathname,
      }));
      setError(error instanceof Error ? error.message : 'Failed to sign in');
    }
  };

  const signOut = async () => {
    try {
      setError(null);

      // Clear local state
      setUser(null);
      setCreator(null);
      setProfileFetched(false);
      setCurrentToken(null);

      // Clear local storage
      localStorage.removeItem(ADVANCED_CONFIG.STORAGE.AUTH_TOKEN_KEY);
      localStorage.removeItem(ADVANCED_CONFIG.STORAGE.USER_PREFERENCES_KEY);

      await logtoSignOut(`${window.location.origin}${AUTH_CONFIG.UI.LOGOUT_REDIRECT}`);
    } catch (error) {
      console.error('Sign out failed:', error);
      setError(error instanceof Error ? error.message : 'Failed to sign out');
    }
  };

  const getAccessToken = async () => {
    try {
      const token = await getLogtoToken();
      return token || null;
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  };

  const refreshProfile = useCallback(async () => {
    if (logtoAuthenticated) {
      setProfileFetched(false);
      await fetchUserProfile();
    }
  }, [logtoAuthenticated, fetchUserProfile]);

  const value = {
    isAuthenticated: logtoAuthenticated && !!user,
    user,
    creator,
    error,
    loading: isLoading || profileLoading,
    signIn,
    signOut,
    getAccessToken,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Main Auth Provider
export const AuthProvider = ({ children }) => {
  // Check if auth is enabled
  if (!AUTH_CONFIG.ENABLED) {
    // Provide a minimal auth context for non-auth mode
    const value = {
      isAuthenticated: false,
      user: null,
      creator: null,
      error: null,
      loading: false,
      signIn: () => Promise.resolve(),
      signOut: () => Promise.resolve(),
      getAccessToken: () => Promise.resolve(null),
      refreshProfile: () => Promise.resolve(),
    };
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
  }

  const config = {
    endpoint: AUTH_CONFIG.LOGTO.ENDPOINT,
    appId: AUTH_CONFIG.LOGTO.APP_ID,
    scopes: AUTH_CONFIG.LOGTO.SCOPES,
    resources: AUTH_CONFIG.LOGTO.RESOURCES,
  };

  // Check if Logto is configured
  if (!config.endpoint || !config.appId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Authentication Not Configured</h2>
            <p className="text-gray-600 mb-4">
              Logto authentication is not properly configured. Please set up your environment variables.
            </p>
            <div className="text-left bg-gray-100 p-4 rounded-md mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Required environment variables:</p>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• VITE_LOGTO_ENDPOINT</li>
                <li>• VITE_LOGTO_APP_ID</li>
              </ul>
            </div>
            <p className="text-xs text-gray-500">
              Check your .env file or environment configuration.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <LogtoProvider config={config}>
      <InnerAuthProvider>{children}</InnerAuthProvider>
    </LogtoProvider>
  );
};
